import { useEffect, useRef } from 'react'
import { useRouter } from 'next/router'

/**
 * Strips the locale prefix from a URL so keys are consistent regardless of
 * whether the URL comes from router.asPath (/space/...) or the routeChangeComplete
 * event (/en/space/...).
 */
const stripLocale = (url: string, locales: string[]): string => {
  for (const locale of locales) {
    if (url.startsWith(`/${locale}/`)) return url.slice(locale.length + 1)
    if (url === `/${locale}`) return '/'
  }
  return url
}

/**
 * Handles scroll behavior on page navigation:
 * - Forward navigation (link/push): scroll to top
 * - Browser back/forward (popstate): restore previous scroll position
 *
 * The body is the effective scroll container instead of the window.
 */
const useScrollRestoration = () => {
  const router = useRouter()
  const isPopState = useRef(false)
  const scrollPositions = useRef<Record<string, number>>({})

  useEffect(() => {
    if (typeof window === 'undefined') return

    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual'
    }

    const locales = router.locales ?? []

    const getScrollY = (): number =>
      document.body.scrollTop || document.documentElement.scrollTop || 0

    const handleRouteChangeStart = () => {
      scrollPositions.current[stripLocale(router.asPath, locales)] = getScrollY()
    }

    router.beforePopState(() => {
      isPopState.current = true
      return true
    })

    const handleRouteChangeComplete = (url: string) => {
      if (isPopState.current) {
        const saved = scrollPositions.current[stripLocale(url, locales)]
        if (saved !== undefined) {
          // defer to let the page render before restoring position
          requestAnimationFrame(() => (document.body.scrollTop = saved))
        }
      } else {
        document.body.scrollTop = 0
      }
      isPopState.current = false
    }

    const handleRouteChangeError = () => {
      isPopState.current = false
    }

    router.events.on('routeChangeStart', handleRouteChangeStart)
    router.events.on('routeChangeComplete', handleRouteChangeComplete)
    router.events.on('routeChangeError', handleRouteChangeError)

    return () => {
      router.beforePopState(() => true) // reset the popstate event handler
      router.events.off('routeChangeStart', handleRouteChangeStart)
      router.events.off('routeChangeComplete', handleRouteChangeComplete)
      router.events.off('routeChangeError', handleRouteChangeError)
    }
  }, [router])
}

export default useScrollRestoration
